import { action } from "typesafe-actions";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import { IForm } from "./forms.types";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";

// Action Types
export const FETCH_FORM_LIST_PROGRESS = "FETCH_FORM_LIST_PROGRESS";
export const FETCH_FORM_LIST_SUCCESS = "FETCH_FORM_LIST_SUCCESS";
export const FETCH_FORM_LIST_FAILED = "FETCH_FORM_LIST_FAILED";

export const FETCH_FORM_PROGRESS = "FETCH_FORM_PROGRESS";
export const FETCH_FORM_SUCCESS = "FETCH_FORM_SUCCESS";
export const FETCH_FORM_FAILED = "FETCH_FORM_FAILED";

// Action Creators
export const fetchFormListProgress = () => action(FETCH_FORM_LIST_PROGRESS);
export const fetchFormListSuccess = (data: IForm[], totalRecords: number) =>
  action(FETCH_FORM_LIST_SUCCESS, { data, totalRecords });
export const fetchFormListFailed = (errorMessage?: string) =>
  action(FETCH_FORM_LIST_FAILED, { errorMessage });

export const fetchFormProgress = () => action(FETCH_FORM_PROGRESS);
export const fetchFormSuccess = (data: IForm) =>
  action(FETCH_FORM_SUCCESS, { data });
export const fetchFormFailed = (errorMessage?: string) =>
  action(FETCH_FORM_FAILED, { errorMessage });

// Async Actions
export const fetchFormListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    company_outlet_uuid: string,
    date?: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
    searchByName?: string,
    companyFilter?: string | null
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let searchQuery = "";

      if ((searchValue?.length ?? 0) > 0 || searchByName || companyFilter) {
        const advanceFilter = [...(searchValue || [])];

        if (searchByName) {
          advanceFilter.push({
            column: ["form_name"],
            value: searchByName,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (companyFilter) {
          advanceFilter.push({
            column: ["company_outlet_uuid"],
            value: companyFilter,
            logicalOperator: "AND",
            operator: "EQUALS",
          });
        }

        searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      if (date) {
        searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      }

      if (status && status !== "-1") {
        searchQuery += `&status=${status}`;
      }

      const finalUrl =
        `/dataManagement/get-form?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&company_outlet_uuid=${company_outlet_uuid}` +
        searchQuery;

      dispatch(fetchFormListProgress());
      dispatch(saveLoaderProgress());
      const res = await api.get(finalUrl);
      const data: IForm[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchFormListSuccess(data, totalRecords));
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || "Failed to fetch forms";
      dispatch(fetchFormListFailed(errorMessage));

      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchFormAsync =
  (
    formUuid: string,
    company_outlet_uuid: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchFormProgress());
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/dataManagement/get-form?form_uuid=${formUuid}&company_outlet_uuid=${company_outlet_uuid}`
      );
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchFormSuccess(data[0]));
      } else {
        dispatch(fetchFormFailed("Failed to fetch form"));
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || "Failed to fetch form";
      dispatch(fetchFormFailed(errorMessage));
      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertFormAsync =
  (
    form: IForm,
    onCallback: (isSuccess?: boolean, data?: IForm) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const {
        create_ts,
        insert_ts,
        created_by_uuid,
        created_by_name,
        modified_by_uuid,
        modified_by_name,
        ...rest
      } = form;

      const res = await api.post("/dataManagement/upsert-form", rest);
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallback(true, data);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || "Failed to save form";
      onCallback(false, errorMessage);
      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_FORM_STATE = "CLEAR_FORM_STATE";
export const CLEAR_FORM = "CLEAR_FORM";

export const clearFormState = () => action(CLEAR_FORM_STATE);
export const clearForm = () => action(CLEAR_FORM);
